import { useMembershipCta } from "@/hooks/useMembershipCta";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Crown, Check } from "lucide-react";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

type Props = {
  variant?: "hero" | "compact";
  onDecideLater?: () => void;
  decideLaterHref?: string;
  /** Where the primary CTA leads. Defaults to the single membership journey
   *  (create account → verify email → choose provider → pay). */
  ctaHref?: string;
  /** Analytics label for the CTA that started the journey. */
  ctaId?: string;
  source?: string;
};

const BULLETS = [
  "$14.99 per month — cancel anytime, no contract.",
  "Pay securely with card (Stripe) or PayPal.",
  "Create your account first — no payment taken until step 4.",
  "Immediate access to your Recovery Space once membership is active.",
];


export function FoundingMembershipCTA({
  variant = "hero",
  onDecideLater,
  decideLaterHref,
  ctaHref = MEMBERSHIP_JOURNEY_PATH,
  ctaId = "founding_membership_cta",
  source = "founding_membership_cta",
}: Props) {
  const { showJoinCta } = useMembershipCta();
  if (!showJoinCta) return null;


  const isHero = variant === "hero";

  return (
    <div
      className={`mx-auto w-full max-w-[720px] rounded-[2.25rem] border border-gold/40 bg-gradient-to-b from-[var(--gold-soft)]/50 via-card/95 to-card/90 text-center shadow-[0_30px_80px_-30px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] backdrop-blur transition-transform duration-500 hover:-translate-y-0.5 ${
        isHero ? "px-6 py-10 sm:px-12 sm:py-14" : "px-5 py-8 sm:px-8"
      }`}
    >
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
        <Crown className="h-7 w-7 text-foreground" strokeWidth={2.25} />
      </div>

      <p className="mt-5 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background/70 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--gold-deep)]">
        Membership
      </p>

      <h2
        className={`mt-5 font-display text-foreground ${
          isHero ? "text-3xl leading-tight sm:text-[38px]" : "text-2xl sm:text-3xl"
        }`}
      >
        Secure your place
      </h2>

      <p
        className={`mx-auto mt-4 max-w-lg leading-relaxed text-muted-foreground ${
          isHero ? "text-[16px] sm:text-[17px]" : "text-[15px]"
        }`}
      >
        Recovery Trauma™ is live — your Membership starts the moment you join.
      </p>

      <ul className="mx-auto mt-7 flex max-w-md flex-col gap-3 text-start">
        {BULLETS.map((b) => (
          <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground/85">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
              <Check className="h-3 w-3 text-foreground" strokeWidth={3} />
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
        <Link
          to={ctaHref}
          onClick={() => startMembershipJourney(ctaId, source)}

          className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 py-5 font-display text-[17px] font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-16px_color-mix(in_oklab,var(--gold-deep)_55%,transparent)] sm:w-auto sm:px-12 sm:text-[18px]"
        >
          Join Recovery Trauma™
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
        </Link>

        <p className="text-sm leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">$14.99 / month</span> · Cancel anytime ·
          Immediate access after joining
        </p>





        <Link
          to="/membership-benefits"
          className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2 text-sm font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
        >
          See Everything Included ↓
        </Link>

        {onDecideLater ? (
          <button
            type="button"
            onClick={onDecideLater}
            className="rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Continue as Guest
          </button>
        ) : (
          <Link
            to={decideLaterHref ?? "/guest"}
            className="rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Continue as Guest
          </Link>
        )}
      </div>

    </div>
  );
}
